// ── marketData.js ──────────────────────────────────────────
// Replaces FMP (legacy endpoints killed Aug 2025) with:
//   • Alpha Vantage — company overview, P/E, P/B, beta, sector
//     Calls /api/alpha-proxy (server-side) — key never in client bundle.
//   • Yahoo Finance v8/v10 — live price, fundamentals (no key needed)
//
// Set ALPHA_KEY (no REACT_APP_ prefix) in Vercel env vars.
// Alpha Vantage free tier: 25 requests/day, 5/min.

import { authFetch } from "../lib/authFetch";
import { supabase, hasSupabase } from "../lib/supabaseClient";

// ── Simple in-memory cache (5 min TTL) ────────────────────
const cache   = {};
const CACHE_MS = 5 * 60 * 1000;

// ── Supabase OVERVIEW cache (24h TTL) ─────────────────────
// Shared across all users — preserves the 25 req/day Alpha quota.
// IMPORTANT: only called post-auth, and all DB queries are wrapped
// in a 2s timeout so a slow/unreachable Supabase never blocks the UI.
const SUPABASE_TTL_MS  = 24 * 60 * 60 * 1000;
const SUPABASE_CACHE_TIMEOUT_MS = 2000;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("cache_timeout")), ms)),
  ]);
}

async function getAlphaOverview(ticker) {
  const symbol = ticker === "BRK" ? "BRK.B" : ticker;

  // 1. In-memory cache (fastest — avoids Supabase round-trip this session)
  const memKey = `/api/alpha-proxy?function=OVERVIEW&symbol=${symbol}`;
  const memHit = fromCache(memKey);
  if (memHit) return memHit;

  // 2. Supabase persistent cache (24h TTL, 2s timeout — never blocks auth/prefs)
  // Uses .maybeSingle() — returns null for 0 rows (cache miss) without a 406 error.
  // .single() would throw PGRST116 (406) on a cache miss or when the anon role has
  // no RLS access (pre-auth queries), both of which are expected non-error states.
  if (hasSupabase) {
    try {
      const { data: row } = await withTimeout(
        supabase
          .from("alpha_overview_cache")
          .select("data, fetched_at")
          .eq("ticker", symbol)
          .maybeSingle(),
        SUPABASE_CACHE_TIMEOUT_MS
      );

      if (row) {
        const age = Date.now() - new Date(row.fetched_at).getTime();
        if (age < SUPABASE_TTL_MS) {
          toCache(memKey, row.data);
          return row.data;
        }
      }
    } catch {
      // Non-fatal (timeout or network) — fall through to live API
    }
  }

  // 3. Live API call
  const params = { function: "OVERVIEW", symbol };
  const url = new URL("/api/alpha-proxy", window.location.origin);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

  const res = await authFetch(url.toString());
  if (!res.ok) throw new Error(`Alpha proxy HTTP ${res.status}`);
  const data = await res.json();

  if (data?.Note)              throw new Error("Alpha Vantage rate limit hit (5/min). Wait a moment.");
  if (data?.Information)       throw new Error("Alpha Vantage daily limit reached (25/day on free tier).");
  if (data?.["Error Message"]) throw new Error(data["Error Message"]);

  toCache(memKey, data);

  // Persist to Supabase (fire-and-forget — never blocks the UI)
  if (hasSupabase) {
    supabase
      .from("alpha_overview_cache")
      .upsert({ ticker: symbol, data, fetched_at: new Date().toISOString() }, { onConflict: "ticker" })
      .then(({ error }) => {
        if (error) console.warn("[alpha cache] upsert failed:", error.message);
      });
  }

  return data;
}

function fromCache(key) {
  const hit = cache[key];
  return hit && Date.now() - hit.ts < CACHE_MS ? hit.data : null;
}
function toCache(key, data) {
  cache[key] = { data, ts: Date.now() };
  return data;
}

// ── Proxy helper (reuses your existing /api/proxy) ────────
function proxyUrl(url) {
  return `/api/proxy?url=${encodeURIComponent(url)}`;
}

// ── Alpha Vantage fetch helper (via server-side proxy) ────
async function alphaFetch(params) {
  const url = new URL("/api/alpha-proxy", window.location.origin);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

  const cacheKey = url.toString();
  const cached   = fromCache(cacheKey);
  if (cached) return cached;

  const res  = await authFetch(url.toString());
  if (!res.ok) throw new Error(`Alpha proxy HTTP ${res.status}`);
  const data = await res.json();

  if (data?.Note)              throw new Error("Alpha Vantage rate limit hit (5/min). Wait a moment.");
  if (data?.Information)       throw new Error("Alpha Vantage daily limit reached (25/day on free tier).");
  if (data?.["Error Message"]) throw new Error(data["Error Message"]);

  return toCache(cacheKey, data);
}

// ── Yahoo Finance helpers (no key needed) ─────────────────
async function yahooQuote(ticker) {
  const t   = ticker === "BRK" ? "BRK-B" : ticker;
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${t}?interval=1d&range=1d`;
  const res = await authFetch(proxyUrl(url));
  if (!res.ok) throw new Error(`Yahoo HTTP ${res.status}`);
  const json   = await res.json();
  const result = json?.chart?.result?.[0];
  if (!result) throw new Error("No Yahoo quote data");
  const meta = result.meta ?? {};
  return {
    price:     meta.regularMarketPrice ?? null,
    marketCap: meta.marketCap          ?? null,
    currency:  meta.currency           ?? "USD",
  };
}

async function yahooSummary(ticker) {
  const t       = ticker === "BRK" ? "BRK-B" : ticker;
  const modules = "summaryDetail,defaultKeyStatistics,financialData,price";
  const url     = `https://query1.finance.yahoo.com/v10/finance/quoteSummary/${t}?modules=${modules}`;
  const res     = await authFetch(proxyUrl(url));
  if (!res.ok) return null;
  const json = await res.json();
  if (json?.quoteSummary?.error) return null;
  return json?.quoteSummary?.result?.[0] ?? null;
}

// ── Company profile via Alpha Vantage OVERVIEW ────────────
export async function fetchProfile(ticker) {
  const data = await getAlphaOverview(ticker);

  if (!data?.Symbol) throw new Error(`No Alpha Vantage overview for ${ticker}`);

  // Also grab live price from Yahoo (Alpha overview price is EOD only)
  let livePrice = null;
  try {
    const q = await yahooQuote(ticker);
    livePrice = q.price;
  } catch { /* fallback to null */ }

  return {
    ticker:      data.Symbol,
    name:        data.Name               ?? ticker,
    sector:      data.Sector             ?? "Unknown",
    industry:    data.Industry           ?? "",
    description: data.Description        ?? "",
    website:     data.OfficialSite       ?? "",
    price:       livePrice,
    pe:          parseFloatSafe(data.TrailingPE),
    pb:          parseFloatSafe(data.PriceToBookRatio),
    beta:        parseFloatSafe(data.Beta),
    dividend:    parseFloatSafe(data.DividendYield) != null
                   ? parseFloat((parseFloatSafe(data.DividendYield) * 100).toFixed(2))
                   : 0,
    marketCap:   parseIntSafe(data.MarketCapitalization),
    eps:         parseFloatSafe(data.EPS),
    evEbitda:    parseFloatSafe(data.EVToEBITDA),
    roe:         parseFloatSafe(data["ReturnOnEquityTTM"]) != null
                   ? parseFloat((parseFloatSafe(data["ReturnOnEquityTTM"]) * 100).toFixed(2))
                   : null,
    debtToEquity: parseFloatSafe(data.DebtToEquityRatio),
    analystTarget: parseFloatSafe(data.AnalystTargetPrice)
                     ? { targetMean: parseFloatSafe(data.AnalystTargetPrice) }
                     : null,
    forwardPE:    parseFloatSafe(data.ForwardPE),
    profitMargin: parseFloatSafe(data.ProfitMargin) != null
                    ? parseFloat((parseFloatSafe(data.ProfitMargin) * 100).toFixed(2))
                    : null,
    revenue:      parseIntSafe(data.RevenueTTM),
    high52:       parseFloatSafe(data["52WeekHigh"]),
    low52:        parseFloatSafe(data["52WeekLow"]),
  };
}

// ── Key metrics — already included in OVERVIEW, but kept ──
// for API compatibility with useMarketData callers
export async function fetchKeyMetrics(ticker) {
  try {
    const data = await getAlphaOverview(ticker);
    return {
      evEbitda:     parseFloatSafe(data.EVToEBITDA),
      roeTTM:       parseFloatSafe(data["ReturnOnEquityTTM"]),
      debtToEquity: parseFloatSafe(data.DebtToEquityRatio),
      currentRatio: parseFloatSafe(data.CurrentRatio),
      revenuePerShare: parseFloatSafe(data.RevenuePerShareTTM),
    };
  } catch {
    return {};
  }
}

// ── Price target — from Alpha OVERVIEW AnalystTargetPrice ─
export async function fetchPriceTarget(ticker) {
  try {
    const data   = await getAlphaOverview(ticker);
    const target = parseFloatSafe(data.AnalystTargetPrice);
    if (!target) return null;
    return {
      targetMean:   target,
      targetHigh:   target,
      targetLow:    target,
      targetMedian: target,
    };
  } catch {
    return null;
  }
}

// ── Full enriched stock (profile + yahoo fundamentals) ────
export async function fetchEnrichedStock(ticker, fallback = {}) {
  try {
    // Run Alpha overview + Yahoo summary in parallel
    const [alphaResult, yahooResult] = await Promise.allSettled([
      fetchProfile(ticker),
      yahooSummary(ticker),
    ]);

    const p = alphaResult.status === "fulfilled" ? alphaResult.value : {};
    const y = yahooResult.status === "fulfilled" ? yahooResult.value : null;

    // Prefer Yahoo live fundamentals when available (more up-to-date)
    const raw = (obj, key) => obj?.[key]?.raw;
    const pe  = raw(y?.summaryDetail, "trailingPE")         ?? p.pe          ?? fallback.pe       ?? 20;
    const pb  = raw(y?.defaultKeyStatistics, "priceToBook") ?? p.pb          ?? fallback.pb       ?? 3;
    const evEbitda = raw(y?.defaultKeyStatistics, "enterpriseToEbitda") ?? p.evEbitda ?? fallback.evEbitda ?? 12;
    const beta     = raw(y?.summaryDetail, "beta")          ?? p.beta        ?? fallback.beta     ?? 1;
    const price    = raw(y?.price, "regularMarketPrice")    ?? p.price       ?? null;

    // Expected return from analyst target
    let expectedReturn = fallback.expectedReturn ?? 10;
    const targetMean   = p.analystTarget?.targetMean ?? null;
    const dividend     = p.dividend ?? fallback.dividend ?? 0;
    if (targetMean && price && price > 0) {
      expectedReturn = parseFloat(
        (((targetMean - price) / price) * 100 + dividend).toFixed(2)
      );
    }

    const volatility = parseFloat(Math.max(10, beta * 15).toFixed(1));

    return {
      ticker:        p.ticker        ?? ticker,
      name:          p.name          ?? fallback.name    ?? ticker,
      sector:        p.sector        ?? fallback.sector  ?? "Unknown",
      industry:      p.industry      ?? fallback.industry ?? "",
      price,
      pe:            pe  != null ? round(pe,  1) : null,
      pb:            pb  != null ? round(pb,  2) : null,
      evEbitda:      evEbitda != null ? round(evEbitda, 1) : null,
      beta:          round(beta, 2),
      volatility,
      expectedReturn,
      dividend,
      marketCap:     p.marketCap     ?? null,
      analystTarget: p.analystTarget ?? null,
      roeTTM:        p.roe           ?? null,
      debtToEquity:  p.debtToEquity  ?? null,
      forwardPE:     p.forwardPE     ?? null,
      profitMargin:  p.profitMargin  ?? null,
      revenue:       p.revenue       ?? null,
      eps:           p.eps           ?? null,
      high52:        p.high52        ?? null,
      low52:         p.low52         ?? null,
      description:   p.description   ?? "",
    };
  } catch (err) {
    console.warn(`fetchEnrichedStock(${ticker}) failed, using fallback:`, err.message);
    return { ...fallback, ticker, _error: err.message };
  }
}

// ── Batch fetch ────────────────────────────────────────────
// NOTE: Alpha Vantage free = 25 req/day, 5/min.
// We stagger requests 300ms apart to stay under 5/min.
export async function fetchMultipleStocks(tickers, fallbacks = {}) {
  const results = {};
  for (let i = 0; i < tickers.length; i++) {
    const t = tickers[i];
    results[t] = await fetchEnrichedStock(t, fallbacks[t] ?? {});
    if (i < tickers.length - 1) await sleep(300); // stay under 5 req/min
  }
  return results;
}

// ── Helpers ────────────────────────────────────────────────
function parseFloatSafe(v) {
  if (v == null || v === "None" || v === "-" || v === "") return null;
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}
function parseIntSafe(v) {
  if (v == null || v === "None" || v === "-" || v === "") return null;
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}
function round(n, d) {
  if (n == null || isNaN(n)) return null;
  return parseFloat(Number(n).toFixed(d));
}
function sleep(ms) {
  return new Promise(res => setTimeout(res, ms));
}
